import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ShopProductPreview from '../ShopProductPreview';

/**
 * What the customer gets, pinned beside the form.
 *
 * The card and builder are the REAL storefront components fed the REAL serializer
 * payload, so this cannot drift from the shop. `overrides` lets an unsaved edit
 * show immediately for the things whose effect is unambiguous client-side (the
 * chosen card image, the offered styles) -- everything else waits for the save
 * that produced it, rather than guessing.
 *
 * The chrome themes with the admin, but the preview ITSELF is always on white:
 * it is the shopper's view, and the shopper does not have the admin's dark mode.
 * Letting it follow the theme would make the preview lie.
 *
 * The school selector answers "what does a customer AT THIS SCHOOL see". Today
 * nothing in the shop catalog actually varies by school -- there are no
 * per-school price, shipping or upsell rows on any shop package -- so its honest
 * job is availability, plus catching a bad per-school override the moment one is
 * set from the assign grid.
 */
const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);

const PreviewPanel = ({
  preview, schools, schoolId, onSchoolChange, overrides, styles, busy,
}) => {
  if (!preview) return null;

  const product = preview.product ? { ...preview.product, ...overrides } : null;
  const hidden = preview.schoolId && preview.visibleToSchool === false;

  return (
    // A flex column that can shrink, so the preview's stage below can be given the
    // height left over instead of both of them scrolling (see index.jsx).
    <Paper sx={{
      mt: 1.5,
      display: 'flex',
      flexDirection: 'column',
      // Takes whatever the checklist above leaves, and may shrink below its content
      // so its stage is the one thing that scrolls.
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}
      >
        <Typography variant="subtitle2">What the customer sees</Typography>
        {busy ? <Typography variant="caption" color="text.secondary">updating…</Typography> : null}
      </Stack>
      <Divider />

      <Box sx={{ px: 2, py: 1.5 }}>
        <TextField
          select
          size="small"
          label="Preview as a customer at"
          value={schoolId || ''}
          onChange={(e) => onSchoolChange(e.target.value || null)}
          sx={{ maxWidth: 340 }}
          fullWidth={false}
          error={!!hidden}
          helperText={[
            hidden ? 'This school cannot see the product — it is not assigned to it.' : null,
            preview.shippingPrice != null ? `Shipping ${money(preview.shippingPrice)}.` : 'No shipping charged.',
            preview.upsellSteps?.length
              ? `After adding: ${preview.upsellSteps.join(' → ')}.`
              : 'Nothing offered after adding.',
          ].filter(Boolean).join(' ')}
        >
          <MenuItem value="">
            {schools.length ? 'Standard price (no school)' : 'No school assigned yet'}
          </MenuItem>
          {schools.map((school) => (
            <MenuItem key={school.id} value={school.id}>{school.name}</MenuItem>
          ))}
        </TextField>
      </Box>
      <Divider />

      {/* Always white: this is the shopper's view, not the admin's. */}
      <Box sx={{
        p: 0.75, bgcolor: '#fff', colorScheme: 'light', flex: 1, minHeight: 0, display: 'flex',
      }}
      >
        <ShopProductPreview
          product={product}
          templates={styles}
          samplePose={preview.samplePose}
          backgroundUrl={preview.backgroundUrl}
          studentName={preview.studentName}
        />
      </Box>
      <Divider />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
        The photo is a placeholder — a real student&rsquo;s pose appears in its place.
      </Typography>
    </Paper>
  );
};

PreviewPanel.propTypes = {
  preview: PropTypes.shape({
    product: PropTypes.shape({ variants: PropTypes.arrayOf(PropTypes.object) }),
    samplePose: PropTypes.shape({ watermark_url: PropTypes.string }),
    backgroundUrl: PropTypes.string,
    studentName: PropTypes.string,
    schoolId: PropTypes.number,
    visibleToSchool: PropTypes.bool,
    shippingPrice: PropTypes.number,
    upsellSteps: PropTypes.arrayOf(PropTypes.string),
  }),
  schools: PropTypes.arrayOf(PropTypes.object),
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSchoolChange: PropTypes.func.isRequired,
  // Unsaved edits whose preview effect is unambiguous, merged over the payload.
  overrides: PropTypes.objectOf(PropTypes.any),
  styles: PropTypes.arrayOf(PropTypes.object),
  busy: PropTypes.bool,
};

PreviewPanel.defaultProps = {
  preview: null, schools: [], schoolId: null, overrides: {}, styles: [], busy: false,
};

export default PreviewPanel;
